<script setup>
import { onMounted, ref } from "vue";
import moment from "moment";
import InfoHolder from "@/components/global/drawer/InfoHolder.vue";
import { useLivelihoodStore } from "@/stores";
import { useRoute, useRouter } from "vue-router";

const livelihood = ref({});

const route = useRoute();
const router = useRouter();

const goToEdit = () => {
  router.push("/livelihood/edit/" + route.params.id);
};

const livelihoodStore = useLivelihoodStore();

const formatDate = (date) => {
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
};

onMounted(async () => {
  const data = await livelihoodStore.getLivelihood({ id: route.params.id });
  livelihood.value = data;
});
</script>

<template>
  <div
    v-if="Object.keys(livelihood).length > 0"
    class="pa-5"
  >
    <div class="d-flex align-center justify-space-between">
      <div>
        <h1>{{ livelihood.name }}</h1>
      </div>
      <div>
        <v-btn
          size="small"
          color="employability"
          class="mr-5"
          @click="goToEdit"
        >
          <v-icon>mdi-pencil</v-icon> edit livelihood
        </v-btn>
      </div>
    </div>
    <v-divider />
    <div class="d-flex box-holder">
      <div class="d-flex box-row flex-1-0-0">
        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-briefcase</v-icon> Livelihood Data
          </h3>
          <InfoHolder
            title="Livelihood Title"
            :info="livelihood.name"
          />
          <InfoHolder
            title="Date"
            :info="formatDate(livelihood.date)"
          />
          <div class="info pa-2">
            <div class="label mb-1">
              Beneficiary Name
            </div>
            <div class="answer">
              <router-link
                :to="'/beneficiary/intake/view/' + livelihood.beneficiary.id"
                target="_blank"
                class="chip-link-color"
              >
                <v-chip> {{ livelihood.beneficiary.name }} </v-chip>
              </router-link>
            </div>
          </div>
          <InfoHolder
            title="Branch"
            :info="livelihood.branch?.name"
          />
          <InfoHolder
            title="Responsible Officer"
            :info="livelihood.responsibleOfficer?.name"
          />

          <div
            v-if="livelihood.completedCourse"
            class="info pa-2"
          >
            <div class="label mb-1">
              What is the training you completed at Odyssea?
            </div>
            <div class="answer">
              <router-link
                :to="'/academy/view/' + livelihood.completedCourse.id"
                target="_blank"
                class="chip-link-color-academy"
              >
                <v-chip> {{ livelihood.completedCourse.name }} </v-chip>
              </router-link>
            </div>
          </div>

          <InfoHolder
            title="Date"
            :info="formatDate(livelihood.date)"
          />

          <InfoHolder
            title="Are you currently working"
            :info="livelihood.currentlyWorking?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'NO_BUT_LOOKING'"
            title="How many job applications have you sent the last 3 months?"
            :info="livelihood.applicationCount"
            type="boolean"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'NO_BUT_LOOKING'"
            title="How many interview invitations did you receive the last 3 months?"
            :info="livelihood.invitationCount"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'NO_BUT_LOOKING'"
            title="In case you did not receive feedback, what do you think went wrong?"
            :info="livelihood.noFeedbackComment"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="Did Odyssea support him/her in finding employment/up-skilling/scholarship?"
            :info="livelihood.odysseaSupport"
            type="boolean"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="What is your current employment status?"
            :info="livelihood.employmentStatus?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="How did you find the job?"
            :info="livelihood.jobFoundMethod.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="When did you start the job?"
            :info="
              livelihood.jobStartDate
                ? formatDate(livelihood.jobStartDate)
                : null
            "
          />

          <InfoHolder
            title="Date that job ended"
            :info="
              livelihood.jobEndDate ? formatDate(livelihood.jobEndDate) : null
            "
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="What is your job title?"
            :info="livelihood.jobTitle"
          />

          <InfoHolder
            v-if="livelihood.company"
            title="Company that hired him"
            :info="livelihood.company.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="Other company not in our list"
            :info="livelihood.otherCompany"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="Relation to the training"
            :info="livelihood.trainingRelation?.name"
          />

          <InfoHolder
            title="In what field they found employment?"
            :info="livelihood.employmentField?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="ISCO-08 Code Level 4"
            :info="livelihood.iscoCodeFour?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="ISCO-08 Code Level 2"
            :info="livelihood.iscoCodeTwo?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="What is your type of contract"
            :info="livelihood.contractType?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="What is the duration of contract"
            :info="livelihood.contractDuration?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="Do you have social insurance?"
            :info="livelihood.socialInsurance"
            type="boolean"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="What is your salary range?"
            :info="livelihood.salaryRange?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'FOUND_JOB'"
            title="Are you satisfied with your work environment and overall working experience?"
            :info="livelihood.workEnvironmentSatisfaction?.name"
          />

          <InfoHolder
            v-if="livelihood.currentlyWorking?.value === 'NO_NOT_LOOKING'"
            title="Why are you not currently searching for a job?"
            :info="livelihood.noSearchReason"
          />

          <div
            v-if="livelihood.suitableJobs?.length > 0"
            class="info pa-2"
          >
            <p>
              <span class="label mb-1">Suitable Jobs</span>
              <span class="answer">
                <v-chip
                  v-for="jobs in livelihood.suitableJobs"
                  :key="jobs.id"
                >
                  {{ jobs.name }}
                </v-chip>
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h1 {
  font-size: 48px;
  color: rgb(var(--v-theme-employability));
}
.box-holder {
  gap: 20px;
  .box-row {
    flex-direction: column;
    .box-w3 {
      width: 100%;
    }
    .box-container {
      border: 1px solid rgb(var(--v-theme-employability));
      h3 {
        background-color: rgb(var(--v-theme-employability));
        color: black;
      }
      .info {
        .label {
          text-decoration: underline;
        }
        .answer {
          display: block;
          font-weight: bold;
        }
      }
    }
  }
}
</style>
